import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import clsx from "clsx";
export function SearchableCombobox({ label, value, onChange, items, placeholder = "Search…", disabled, }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [rect, setRect] = useState(null);
    const filtered = useMemo(() => {
        if (!q.trim())
            return items.slice(0, 50);
        const needle = q.toLowerCase();
        return items
            .filter((i) => i.label.toLowerCase().includes(needle) || i.hint?.toLowerCase().includes(needle))
            .slice(0, 50);
    }, [q, items]);
    const selected = items.find((i) => i.value === value);
    useLayoutEffect(() => {
        if (!open)
            return;
        const compute = () => {
            const el = triggerRef.current;
            if (!el)
                return;
            const r = el.getBoundingClientRect();
            const panelHeight = 320;
            const spaceBelow = window.innerHeight - r.bottom;
            const placeAbove = spaceBelow < panelHeight && r.top > spaceBelow;
            setRect({
                top: placeAbove ? r.top - 4 : r.bottom + 4,
                left: r.left,
                width: r.width,
                placeAbove,
            });
        };
        compute();
        window.addEventListener("resize", compute);
        window.addEventListener("scroll", compute, true);
        return () => {
            window.removeEventListener("resize", compute);
            window.removeEventListener("scroll", compute, true);
        };
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => {
            const target = e.target;
            if (wrapRef.current?.contains(target))
                return;
            if (panelRef.current?.contains(target))
                return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    return (_jsxs("div", { className: "block", ref: wrapRef, children: [label && _jsx("span", { className: "block text-xs font-medium text-slate-600 mb-1", children: label }), _jsxs("button", { ref: triggerRef, type: "button", disabled: disabled, onClick: () => setOpen((v) => !v), className: clsx("flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-left text-sm transition focus:ring-2 focus:ring-brand-100 outline-none", disabled ? "border-slate-100 text-slate-400" : "border-slate-200 text-slate-900 hover:border-slate-300"), children: [_jsx("span", { className: clsx("truncate", !selected && "text-slate-400"), children: selected?.label ?? placeholder }), _jsx(ChevronDown, { size: 14, className: "text-slate-400 ml-2" })] }), open && rect && createPortal(_jsxs("div", { ref: panelRef, style: {
                    position: "fixed",
                    top: rect.placeAbove ? undefined : rect.top,
                    bottom: rect.placeAbove ? window.innerHeight - rect.top : undefined,
                    left: rect.left,
                    width: rect.width,
                    zIndex: 1000,
                }, className: "rounded-lg border border-slate-200 bg-white shadow-lg ring-1 ring-slate-200/60", children: [_jsxs("div", { className: "flex items-center gap-2 border-b border-slate-100 px-3 py-2", children: [_jsx(Search, { size: 14, className: "text-slate-400" }), _jsx("input", { autoFocus: true, value: q, onChange: (e) => setQ(e.target.value), placeholder: "Search\u2026", className: "w-full bg-transparent text-sm outline-none placeholder:text-slate-400" })] }), _jsxs("ul", { className: "max-h-64 overflow-y-auto py-1 text-sm", children: [filtered.length === 0 && (_jsx("li", { className: "px-3 py-2 text-slate-400", children: "No matches" })), filtered.map((i) => (_jsxs("li", { onClick: () => {
                                    onChange(i.value);
                                    setOpen(false);
                                    setQ("");
                                }, className: clsx("cursor-pointer px-3 py-2 hover:bg-slate-50", i.value === value && "bg-brand-50/60 text-brand-700"), children: [_jsx("div", { className: "font-medium text-slate-800", children: i.label }), i.hint && _jsx("div", { className: "text-xs text-slate-500", children: i.hint })] }, i.value)))] }), value !== null && (_jsx("button", { className: "block w-full border-t border-slate-100 px-3 py-2 text-left text-xs text-slate-500 hover:bg-slate-50", onClick: () => {
                            onChange(null);
                            setOpen(false);
                            setQ("");
                        }, children: "Clear selection" }))] }), document.body)] }));
}
